// Account legal documents (markdown hosted on public CDN).
// requiredVersion must match backend/src/config/legal-policies.js when policies change.

/** Public CDN base for revolt-site/legal/*.md */
export const LEGAL_DOCUMENTS_CDN_BASE =
  'https://bluedrake42.nyc3.cdn.digitaloceanspaces.com/revolt-site/legal';

export const legalDocuments = [
  {
    slug: 'terms-of-service',
    displayName: 'Terms of Service',
    listIconClass: 'fa-solid fa-file-contract',
    acceptanceKey: 'termsOfService',
    requiredVersion: '2026-05-19',
  },
  {
    slug: 'privacy-policy',
    displayName: 'Privacy Policy',
    listIconClass: 'fa-solid fa-user-shield',
    acceptanceKey: 'privacyPolicy',
    requiredVersion: '2026-05-19',
  },
  {
    slug: 'refund-policy',
    displayName: 'Refund Policy',
    listIconClass: 'fa-solid fa-rotate-left',
    acceptanceKey: 'refundPolicy',
    requiredVersion: '2026-05-19',
  },
  {
    slug: 'cancellation-policy',
    displayName: 'Cancellation Policy',
    listIconClass: 'fa-solid fa-ban',
    acceptanceKey: 'cancellationPolicy',
    requiredVersion: '2026-05-19',
  },
];

export function getLegalDocumentBySlug(slug) {
  return legalDocuments.find((d) => d.slug === slug) ?? null;
}

/**
 * CDN URL for an allowlisted legal markdown file.
 * @param {string} slug
 * @returns {string}
 */
export function getLegalDocumentCdnUrl(slug) {
  const doc = getLegalDocumentBySlug(String(slug ?? '').trim());
  if (!doc) {
    throw new Error('Unknown legal document');
  }
  return `${LEGAL_DOCUMENTS_CDN_BASE}/${doc.slug}.md`;
}

/**
 * Load legal markdown from the public CDN (no backend).
 * @param {string} slug
 * @returns {Promise<{ markdown: string, title: string }>}
 */
export async function fetchLegalDocumentMarkdown(slug) {
  const doc = getLegalDocumentBySlug(String(slug ?? '').trim());
  if (!doc) {
    throw new Error('Unknown legal document');
  }

  const response = await fetch(getLegalDocumentCdnUrl(doc.slug));
  if (!response.ok) {
    throw new Error('Failed to load document');
  }

  const markdown = await response.text();
  if (!String(markdown ?? '').trim()) {
    throw new Error('Document is empty');
  }

  return {
    markdown,
    title: doc.displayName,
  };
}

function getOrdinalSuffix(day) {
  if (day >= 11 && day <= 13) return 'th';
  const last = day % 10;
  if (last === 1) return 'st';
  if (last === 2) return 'nd';
  if (last === 3) return 'rd';
  return 'th';
}

/**
 * Display a policy `requiredVersion` stored as YYYY-MM-DD (e.g. "May 19th, 2026").
 * @param {string|null|undefined} version
 * @returns {string}
 */
export function formatLegalPolicyVersion(version) {
  const raw = String(version ?? '').trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!match) return raw || '—';

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!year || month < 1 || month > 12 || day < 1 || day > 31) {
    return raw;
  }

  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return raw;
  }

  const monthName = date.toLocaleString('en-US', { month: 'long' });
  return `${monthName} ${day}${getOrdinalSuffix(day)}, ${year}`;
}

/** Build registration payload booleans keyed by acceptanceKey. */
export function buildRegistrationLegalAcceptance(checkedByKey) {
  const record = {};
  for (const doc of legalDocuments) {
    record[doc.acceptanceKey] = checkedByKey[doc.acceptanceKey] === true;
  }
  return record;
}

export function areAllLegalDocumentsChecked(checkedByKey) {
  return legalDocuments.every((doc) => checkedByKey[doc.acceptanceKey] === true);
}
